import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link2, Copy, Loader2, Sparkles, Trash2, RefreshCw, Search, Wand2, CheckCircle2, Clock, FileText, Pencil, Save, MessageSquarePlus, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  sent: { label: "Enlace enviado", cls: "bg-neutral-500/20 text-neutral-300" },
  draft: { label: "Escribiendo…", cls: "bg-sky-500/20 text-sky-300" },
  submitted: { label: "Información recibida", cls: "bg-emerald-500/20 text-emerald-300" },
};

const copy = async (text, msg = "Copiado") => {
  try { await navigator.clipboard.writeText(text); toast.success(msg); } catch { toast.error("No se pudo copiar"); }
};

const linkMessage = (f) => `Hola ${f.user_name?.split(" ")[0] || ""} 🔮 Para preparar tu lectura de ${f.service_name}, por favor llena esta guía con tus datos y mínimo 6 preguntas:\n${f.url}\nToda tu información es 100% confidencial. — CrisAngel Astral`;

/* ----------------------------------------------------------- generar enlace */
const LinkCreator = ({ onCreated }) => {
  const { authHeader } = useAuth();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [planId, setPlanId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/reading-form/eligible-clients`, { headers: authHeader() }).then((r) => setClients(r.data || [])).catch(() => null);
  }, [authHeader]);

  const list = useMemo(() => clients.filter((c) => !q || `${c.name} ${c.username} ${c.visible_id} ${c.phone}`.toLowerCase().includes(q.toLowerCase())).slice(0, 8), [clients, q]);

  const create = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/reading-form/link`, { user_id: sel.id, plan_id: planId || null }, { headers: authHeader() });
      onCreated(r.data);
      await copy(linkMessage(r.data), "Enlace + mensaje copiados");
      setSel(null); setQ("");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar"); } finally { setBusy(false); }
  };

  return (
    <div className="glass-dark rounded-2xl p-4 space-y-3" data-testid="reading-form-creator">
      <div className="flex items-center gap-2 text-[#d4af37] font-semibold text-sm"><Link2 className="w-4 h-4" /> Generar enlace de guía de lectura</div>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-2.5 text-neutral-500" />
        <input data-testid="rf-client-search" value={q} onChange={(e) => { setQ(e.target.value); setSel(null); }} placeholder="Buscar cliente con servicio activo (nombre, usuario, ID, teléfono)"
          className="w-full bg-black/40 border border-white/10 rounded-xl pl-9 pr-3 py-2 text-sm text-white focus:outline-none focus:border-[#d4af37]/60" />
      </div>
      {!sel && q && (
        <div className="rounded-xl border border-white/10 divide-y divide-white/5 max-h-60 overflow-y-auto">
          {list.length === 0 && <div className="p-3 text-xs text-neutral-500 italic">Sin coincidencias entre clientes con pago aprobado o servicio activo.</div>}
          {list.map((c) => (
            <button key={c.id} type="button" data-testid={`rf-client-${c.id}`} onClick={() => { setSel(c); setPlanId(c.plans?.[0]?.id || ""); }} className="w-full text-left p-2.5 hover:bg-white/5 text-sm">
              <div className="text-white">{c.name} <span className="text-neutral-500 text-xs">@{c.username} · {c.visible_id}</span></div>
              <div className="text-[11px] text-neutral-400">{[...(c.plans || []).map((p) => p.service_name), ...(c.approved_services || [])].filter(Boolean).slice(0, 3).join(" · ")}</div>
            </button>
          ))}
        </div>
      )}
      {sel && (
        <div className="rounded-xl border border-[#d4af37]/30 bg-[#d4af37]/5 p-3 space-y-2 text-sm" data-testid="rf-client-selected">
          <div className="text-white font-medium">{sel.name} <span className="text-neutral-500 text-xs">@{sel.username}</span></div>
          {sel.plans?.length > 0 && (
            <select data-testid="rf-plan-select" value={planId} onChange={(e) => setPlanId(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white">
              {sel.plans.map((p) => <option key={p.id} value={p.id}>{p.service_name}{p.paid ? " · liquidado" : ""}</option>)}
            </select>
          )}
          <button type="button" data-testid="rf-create-link" disabled={busy} onClick={create} className="w-full rounded-full bg-[#d4af37] text-black font-semibold py-2 text-sm hover:bg-[#e6c35a] disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />} Generar y copiar enlace
          </button>
        </div>
      )}
    </div>
  );
};

const FIELD_DEFS = [["full_name", "Nombre completo"], ["birth_date", "Fecha de nacimiento"], ["location", "Ciudad y país"], ["children", "Hijos (nombres y edades)"], ["partner", "Pareja / ex (nombre y nacimiento)"]];

const ClientFieldsEditor = ({ form, onSaved }) => {
  const { authHeader } = useAuth();
  const [f, setF] = useState(form.fields || {});
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!editing) setF(form.fields || {}); }, [form.fields, editing]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/admin/reading-forms/${form.id}/fields`, f, { headers: authHeader() });
      onSaved(r.data); setEditing(false); toast.success("Datos del cliente actualizados");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); } finally { setBusy(false); }
  };
  const cls = "w-full bg-black/40 border border-[#d4af37]/30 rounded-lg px-2 py-1.5 text-sm text-white focus:outline-none focus:border-[#d4af37]";

  if (!editing) {
    return (
      <div className="space-y-2" data-testid="rf-client-info">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Información del cliente {form.edited_by_admin_at && <span className="text-[#d4af37]">· corregida por ti</span>}</div>
          <button type="button" data-testid="rf-edit-fields" onClick={() => setEditing(true)} className="text-[11px] px-2 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-neutral-200 flex items-center gap-1"><Pencil className="w-3 h-3" /> Corregir datos</button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          {FIELD_DEFS.map(([k, label]) => (
            <div key={k} className="rounded-lg bg-black/30 border border-white/5 p-2.5"><div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div><div className="text-neutral-100">{f[k] || <span className="text-neutral-600 italic">—</span>}</div></div>
          ))}
          <div className="rounded-lg bg-black/30 border border-white/5 p-2.5 sm:col-span-2">
            <div className="flex items-center justify-between"><div className="text-[10px] uppercase tracking-wider text-neutral-500">Preguntas</div><span className={`text-[10px] ${form.question_count >= 6 ? "text-emerald-300" : "text-amber-300"}`}>{form.question_count} detectadas</span></div>
            <div className="text-neutral-100 whitespace-pre-wrap text-[13px] leading-relaxed" data-testid="rf-questions-view">{f.questions || <span className="text-neutral-600 italic">El cliente aún no escribe…</span>}</div>
          </div>
          {f.extra && <div className="rounded-lg bg-black/30 border border-white/5 p-2.5 sm:col-span-2"><div className="text-[10px] uppercase tracking-wider text-neutral-500">Contexto adicional</div><div className="text-neutral-100 whitespace-pre-wrap text-[13px]">{f.extra}</div></div>}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2 rounded-xl border border-[#d4af37]/30 p-3" data-testid="rf-fields-editor">
      <div className="text-[#d4af37] text-sm font-semibold">Corregir información del cliente</div>
      <div className="grid sm:grid-cols-2 gap-2">
        {FIELD_DEFS.map(([k, label]) => (
          <label key={k} className="text-[10px] uppercase tracking-wider text-neutral-500">{label}<input data-testid={`rf-edit-${k}`} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={`${cls} mt-0.5`} /></label>
        ))}
      </div>
      <label className="block text-[10px] uppercase tracking-wider text-neutral-500">Preguntas<textarea data-testid="rf-edit-questions" rows={7} value={f.questions || ""} onChange={(e) => setF({ ...f, questions: e.target.value })} className={`${cls} mt-0.5`} /></label>
      <label className="block text-[10px] uppercase tracking-wider text-neutral-500">Contexto adicional<textarea data-testid="rf-edit-extra" rows={3} value={f.extra || ""} onChange={(e) => setF({ ...f, extra: e.target.value })} className={`${cls} mt-0.5`} /></label>
      <div className="flex gap-2">
        <button type="button" data-testid="rf-save-fields" disabled={busy} onClick={save} className="flex-1 rounded-full bg-[#d4af37] text-black font-semibold py-2 text-sm hover:bg-[#e6c35a] disabled:opacity-50 flex items-center justify-center gap-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar datos</button>
        <button type="button" onClick={() => setEditing(false)} className="px-4 rounded-full bg-white/5 text-neutral-300 text-sm">Cancelar</button>
      </div>
    </div>
  );
};

const ExtensionsBox = ({ form, onChange }) => {
  const { authHeader } = useAuth();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const exts = form.extensions || [];

  const ask = async () => {
    if (q.trim().length < 3) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/reading-forms/${form.id}/extend`, { question: q.trim() }, { headers: authHeader() });
      onChange({ ...form, extensions: [...exts, r.data.extension] }); setQ("");
      toast.success("Generando respuesta… 20-60 s");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo extender"); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    await axios.delete(`${API}/admin/reading-forms/${form.id}/extend/${id}`, { headers: authHeader() }).catch(() => null);
    onChange({ ...form, extensions: exts.filter((e) => e.id !== id) });
  };

  return (
    <div className="rounded-xl border border-sky-500/30 bg-sky-500/5 p-3 space-y-3" data-testid="rf-extensions">
      <div className="text-sky-300 font-semibold text-sm flex items-center gap-1.5"><MessageSquarePlus className="w-4 h-4" /> Extender la lectura · preguntas adicionales del cliente</div>
      <p className="text-[11px] text-neutral-400">Si el cliente te pregunta algo en la llamada o quieres profundizar un tema, escríbelo aquí: la IA responde como continuación coherente con las cartas que salieron.</p>
      <div className="flex gap-2">
        <textarea data-testid="rf-extend-input" rows={2} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ej: ¿Mi esposo conseguirá trabajo antes de diciembre? / Extiende sobre su salud en 2027…" className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-xs text-white" />
        <button type="button" data-testid="rf-extend-submit" disabled={busy || !form.reading_text || q.trim().length < 3} onClick={ask} className="px-4 rounded-full bg-sky-500 text-black font-semibold text-xs hover:bg-sky-400 disabled:opacity-40 flex items-center gap-1.5">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Responder</button>
      </div>
      {!form.reading_text && <div className="text-[11px] text-amber-300">Primero genera la lectura base.</div>}
      {exts.map((e, i) => (
        <div key={e.id} className="rounded-lg bg-black/40 border border-white/10 p-3 space-y-1.5" data-testid={`rf-ext-${e.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="text-xs text-sky-200 font-medium">Pregunta {i + 1}: {e.question}</div>
            <div className="flex items-center gap-1 shrink-0">
              {e.answer && <button type="button" onClick={() => copy(e.answer, "Respuesta copiada")} className="text-[10px] px-2 py-0.5 rounded bg-white/5 hover:bg-white/10 text-neutral-200 flex items-center gap-1"><Copy className="w-3 h-3" /></button>}
              <button type="button" onClick={() => remove(e.id)} className="text-[10px] px-2 py-0.5 rounded bg-red-500/10 hover:bg-red-500/20 text-red-300"><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
          {e.status === "generating" && <div className="text-[11px] text-neutral-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Consultando las cartas…</div>}
          {e.status === "error" && <div className="text-[11px] text-red-300">Error: {e.error}</div>}
          {e.answer && <div className="text-[13px] leading-relaxed text-neutral-100 whitespace-pre-wrap">{e.answer}</div>}
        </div>
      ))}
    </div>
  );
};

/* ----------------------------------------------------------- detalle */
const FormDetail = ({ form, onChange, onDelete }) => {
  const { authHeader } = useAuth();
  const [pitch, setPitch] = useState(form.include_work_pitch !== false);
  const [notes, setNotes] = useState(form.admin_notes || "");
  const [busy, setBusy] = useState(false);
  const f = form.fields || {};

  const generate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/reading-forms/${form.id}/generate`, { include_work_pitch: pitch, admin_notes: notes }, { headers: authHeader() });
      onChange({ ...form, reading_status: "generating" });
      toast.success("Generando lectura… tarda 3-5 minutos");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar"); } finally { setBusy(false); }
  };

  return (
    <div className="glass-dark rounded-2xl p-4 space-y-4" data-testid={`rf-detail-${form.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="text-white font-semibold">{form.user_name} <span className="text-neutral-500 text-xs">· {form.service_name}</span></div>
          <div className="text-[11px] text-neutral-500">Creado {new Date(form.created_at).toLocaleString("es-MX")}{form.submitted_at ? ` · enviado ${new Date(form.submitted_at).toLocaleString("es-MX")}` : ""}</div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS[form.status]?.cls}`}>{STATUS[form.status]?.label}</span>
          <button type="button" data-testid="rf-copy-link" onClick={() => copy(linkMessage(form), "Enlace + mensaje copiados")} className="text-[11px] px-2 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-neutral-200 flex items-center gap-1"><Copy className="w-3 h-3" /> Enlace</button>
          <button type="button" data-testid="rf-delete" onClick={onDelete} className="text-[11px] px-2 py-1 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-300"><Trash2 className="w-3 h-3" /></button>
        </div>
      </div>

      <ClientFieldsEditor form={form} onSaved={onChange} />

      <div className="rounded-xl border border-[#d4af37]/30 p-3 space-y-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-[#d4af37] font-semibold text-sm flex items-center gap-1.5"><Wand2 className="w-4 h-4" /> Crear lectura con IA</div>
          <label className="flex items-center gap-2 text-xs text-neutral-200 cursor-pointer">
            <input data-testid="rf-pitch-toggle" type="checkbox" checked={pitch} onChange={(e) => setPitch(e.target.checked)} className="accent-[#d4af37]" />
            Incluir bloqueo energético + Magia Suprema / Poder Máximo
          </label>
        </div>
        <textarea data-testid="rf-admin-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notas para la lectura (opcional): énfasis, lo que viste, tono…" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-xs text-white" />
        <button type="button" data-testid="rf-generate" disabled={busy || form.reading_status === "generating" || !f.questions} onClick={generate}
          className="w-full rounded-full bg-[#d4af37] text-black font-semibold py-2 text-sm hover:bg-[#e6c35a] disabled:opacity-50 flex items-center justify-center gap-2">
          {form.reading_status === "generating" ? <><Loader2 className="w-4 h-4 animate-spin" /> Generando lectura…</> : <><Sparkles className="w-4 h-4" /> {form.reading_text ? "Volver a generar" : "Crear lectura"}</>}
        </button>
        {form.reading_status === "error" && <div className="text-xs text-red-300">Error: {form.reading_error}</div>}
      </div>

      {form.reading_text && (
        <div className="space-y-2" data-testid="rf-reading">
          <div className="flex items-center justify-between">
            <div className="text-sm text-white font-semibold flex items-center gap-1.5"><FileText className="w-4 h-4 text-[#d4af37]" /> Lo que le dirás a {f.full_name?.split(" ")[0] || "tu cliente"}</div>
            <button type="button" data-testid="rf-copy-reading" onClick={() => copy(form.reading_text, "Lectura copiada")} className="text-[11px] px-2 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-neutral-200 flex items-center gap-1"><Copy className="w-3 h-3" /> Copiar lectura</button>
          </div>
          <div className="rounded-xl bg-black/40 border border-white/10 p-4 text-[13px] leading-relaxed text-neutral-100 whitespace-pre-wrap max-h-[32rem] overflow-y-auto">{form.reading_text}</div>
          <div className="text-[10px] text-neutral-500">{form.reading_text.split(/\s+/).length.toLocaleString()} palabras · generada {form.reading_generated_at ? new Date(form.reading_generated_at).toLocaleString("es-MX") : ""} · <Lock className="inline w-3 h-3" /> sólo visible para administradores</div>
        </div>
      )}

      <ExtensionsBox form={form} onChange={onChange} />
    </div>
  );
};

/* ----------------------------------------------------------- sección */
export const AdminReadingFormsSection = () => {
  const { authHeader } = useAuth();
  const [forms, setForms] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/reading-forms`, { headers: authHeader() }); setForms(r.data || []); } catch { /* noop */ } finally { setLoading(false); }
  }, [authHeader]);
  useEffect(() => { load(); }, [load]);

  useRealtimeEvents({
    admin_refresh: (d) => { if (d?.scope === "reading_forms") load(); },
    reading_form_draft: (d) => setForms((arr) => arr.map((f) => (f.id === d.id ? { ...f, fields: { ...f.fields, ...d.fields }, status: d.status || f.status, question_count: ((d.fields?.questions || "").match(/\?/g) || []).length } : f))),
    reading_form_submitted: (d) => { toast.success(`🔮 ${d?.user_name || "Cliente"} envió su información de lectura`); load(); },
    reading_form_ready: () => { toast.success("✨ Lectura generada"); load(); },
  });

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este formulario y su lectura?")) return;
    await axios.delete(`${API}/admin/reading-forms/${id}`, { headers: authHeader() }).catch(() => null);
    setForms((a) => a.filter((f) => f.id !== id)); if (openId === id) setOpenId(null);
  };

  return (
    <div className="space-y-4" data-testid="reading-forms-section">
      <LinkCreator onCreated={(f) => { setForms((a) => [f, ...a]); setOpenId(f.id); }} />
      <div className="flex items-center justify-between">
        <div className="text-sm text-neutral-300 font-medium">Formularios ({forms.length})</div>
        <button type="button" onClick={load} className="text-xs text-neutral-400 hover:text-white flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Actualizar</button>
      </div>
      {loading ? <div className="text-neutral-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
        : forms.length === 0 ? <div className="glass-dark rounded-2xl p-10 text-center text-neutral-500 italic text-sm">Aún no generas ningún enlace de guía de lectura.</div>
          : forms.map((f) => (
            <div key={f.id}>
              {openId === f.id ? (
                <div className="space-y-2">
                  <button type="button" onClick={() => setOpenId(null)} className="text-xs text-neutral-400 hover:text-white">▲ Cerrar</button>
                  <FormDetail form={f} onChange={(nf) => setForms((a) => a.map((x) => (x.id === nf.id ? nf : x)))} onDelete={() => remove(f.id)} />
                </div>
              ) : (
                <button type="button" data-testid={`rf-row-${f.id}`} onClick={() => setOpenId(f.id)} className="w-full text-left glass-dark rounded-xl p-3 hover:border-[#d4af37]/40 border border-transparent transition">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-sm text-white">{f.user_name} <span className="text-neutral-500 text-xs">· {f.service_name}</span></div>
                    <div className="flex items-center gap-1.5 text-[10px]">
                      {f.reading_status === "ready" && <span className="px-2 py-0.5 rounded-full bg-[#d4af37]/20 text-[#d4af37] flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Lectura lista</span>}
                      {f.reading_status === "generating" && <span className="px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-300 flex items-center gap-1"><Clock className="w-3 h-3 animate-pulse" /> Generando</span>}
                      <span className={`px-2 py-0.5 rounded-full ${STATUS[f.status]?.cls}`}>{STATUS[f.status]?.label}</span>
                      <span className="text-neutral-500">{f.question_count} preg.</span>
                    </div>
                  </div>
                </button>
              )}
            </div>
          ))}
    </div>
  );
};

export default AdminReadingFormsSection;
